import { Link } from "react-router-dom";
import { ArrowRight, Linkedin } from "lucide-react";
import { SiteLayout } from "../components/site/SiteLayout";
import { SEO } from "../components/site/SEO";
import { useContent } from "../context/ContentContext";

const VALUES = [
  { title: "Service comes first", body: "The doorstep business is built on reliability and trust. Everything we recommend starts from that principle." },
  { title: "Practical advice", body: "No frameworks for the sake of it. Just honest answers that reflect how the business really works." },
  { title: "Supporting independent businesses", body: "Family-run and independent operators are the backbone of this industry. We work hard to help them thrive." },
  { title: "Backing British farming", body: "A healthy doorstep delivery sector supports the farmers behind it. We take that seriously." },
  { title: "Long-term relationships", body: "The best work happens over time. We'd rather earn trust than win pitches." },
];

export default function About() {
  const { content } = useContent();
  const { contact, founderPhoto } = content;
  return (
    <SiteLayout>
      <SEO
        title="About Fourbees Consultancy — Built on Experience, Driven by Service"
        description="Fourbees Consultancy was founded on over 15 years of hands-on doorstep dairy experience. Honest, practical consultancy for independent dairies and small businesses."
        path="/about"
      />

      {/* Hero */}
      <section className="bg-cream py-24 md:py-32" data-testid="about-hero">
        <div className="container-site">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-7">
              <div className="section-number mb-3">About Fourbees</div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-extrabold text-navy leading-[1.0] tracking-[-0.025em]">
                Built on experience. <br />
                <span className="text-gold">Driven by service.</span>
              </h1>
              <p className="font-body text-base md:text-lg text-charcoal/75 leading-relaxed mt-8 max-w-xl">
                Fourbees Consultancy was established to give independent dairies and small
                businesses the kind of accessible, knowledgeable support we wished existed
                when we were doing the job ourselves.
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="relative">
                <div className="absolute -top-3 -left-3 w-24 h-24 border-2 border-gold hidden md:block" />
                <img
                  src={founderPhoto}
                  alt={`${contact.founderName}, founder of Fourbees Consultancy`}
                  className="relative w-full h-[460px] md:h-[520px] object-cover rounded-sm"
                  data-testid="about-founder-photo"
                />
                <div className="absolute -bottom-4 right-4 bg-navy text-cream px-5 py-3 rounded-sm">
                  <div className="font-heading text-base font-semibold">{contact.founderName}</div>
                  <div className="text-xs text-cream/70 uppercase tracking-wider">
                    {contact.founderRole}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="bg-cream border-t border-light-grey py-24 md:py-28" data-testid="about-story">
        <div className="container-site">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <div className="section-number mb-3">01 — The story</div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy leading-tight tracking-tight">
                Fifteen years on the doorstep, before the consultancy.
              </h2>
            </div>
            <div className="lg:col-span-8 space-y-5 font-body text-base md:text-lg text-charcoal/80 leading-relaxed">
              <p>
                Fourbees Consultancy was founded on over 15 years of hands-on experience within
                the UK doorstep dairy industry, alongside practical transport and logistics
                experience across small-business delivery operations.
              </p>
              <p>
                The business was established to provide independent dairies and other small
                businesses with accessible, knowledgeable and honest consultancy support — the
                kind of help that respects your time, your margins and the realities of running
                a small operation.
              </p>
              <p>
                We understand what happens behind the scenes: early-morning deliveries and route
                planning, customer accounts and awkward refunds, invoicing done at the kitchen
                table and the quiet admin tasks that run on into the evening. That lived
                experience shapes everything we recommend.
              </p>
              <p className="text-navy font-semibold">
                We're not a large faceless consultancy. We're a specialist business offering
                genuine industry knowledge and personal support.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-navy text-cream py-24 md:py-28" data-testid="about-values">
        <div className="container-site">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-10">
            <div className="lg:col-span-5">
              <div className="section-number mb-3">02 — What we stand for</div>
              <h2 className="font-heading text-3xl md:text-5xl font-bold text-cream leading-[1.05] tracking-tight">
                Values that guide the work.
              </h2>
            </div>
            <div className="lg:col-span-7 lg:pt-4">
              <p className="font-body text-cream/70 leading-relaxed max-w-2xl">
                Everything we do comes back to five simple principles. They're not slogans — they're
                the standards we hold ourselves to on every piece of work.
              </p>
            </div>
          </div>
          <div className="divide-y divide-cream/10 border-t border-cream/10">
            {VALUES.map((v, i) => (
              <div
                key={i}
                className="grid grid-cols-1 md:grid-cols-12 gap-6 py-8"
                data-testid={`about-value-${i}`}
              >
                <div className="md:col-span-1 section-number">0{i + 1}</div>
                <div className="md:col-span-4">
                  <h3 className="font-heading text-xl md:text-2xl font-bold text-cream leading-tight">
                    {v.title}
                  </h3>
                </div>
                <div className="md:col-span-7">
                  <p className="font-body text-sm md:text-base text-cream/75 leading-relaxed">
                    {v.body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-cream py-20 md:py-24" data-testid="about-cta">
        <div className="container-site">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-navy leading-tight tracking-tight">
                Rather have a direct conversation first?
              </h2>
              <p className="font-body text-charcoal/70 mt-3 max-w-xl">
                No sales pitch — just a short chat about what you're working on and whether we're
                a good fit.
              </p>
            </div>
            <div className="lg:col-span-4 lg:text-right flex flex-col sm:flex-row lg:justify-end gap-3">
              <Link to="/contact" className="btn-primary" data-testid="about-cta-contact">
                Start a Conversation <ArrowRight size={16} strokeWidth={2.5} />
              </Link>
              <a
                href={contact.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary"
                data-testid="about-cta-linkedin"
              >
                <Linkedin size={16} strokeWidth={2} /> LinkedIn
              </a>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
